/**
 * HttpCore - the single axios instance and bearer-token state shared by every
 * per-domain HTTP API class.
 *
 * Exactly one HttpCore exists per RealAPIClient, and every domain API is
 * constructed with that same core, so a token set once (e.g. after login) is
 * attached to requests from every domain.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { IAuthTokenApi } from '../contracts';
import { isMfaEnrollmentRequiredError, isMustChangePasswordError } from '../../utils/apiError';
import * as storage from '../../utils/storage';
import { loginPathFor } from '../../utils/portal';

/** Route of the forced / self-service password change screen. */
export const CHANGE_PASSWORD_PATH = '/change-password';

/** Route of the security page (MFA setup; also the forced enrollment gate, tracker item 63). */
const SECURITY_PATH = '/account/security';

/** Calls whose 401 is the page's own message to show (a wrong password or code), not an expired session. */
const OWN_401_PATHS = ['/auth/login', '/auth/mfa/verify'];

export class HttpCore implements IAuthTokenApi {
  readonly http: AxiosInstance;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.http = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.http.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor to handle errors
    this.http.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // A wrong password on the login call itself is the login page's own
        // message to show (no reload); any other 401 is an expired session.
        // Customers go back to the portal login, staff to /login.
        const url = String(error.config?.url ?? '');
        if (error.response?.status === 401 && !OWN_401_PATHS.some((path) => url.endsWith(path))) {
          this.clearAuthToken();
          window.location.href = loginPathFor(window.location.pathname);
        } else if (isMustChangePasswordError(error)) {
          // The backend gates every protected route until the temporary
          // password is changed. Persist the flag first so the reloaded app
          // comes up gated (the JWT carries no such claim), and skip the
          // redirect when already there to avoid a reload loop.
          storage.saveMustChangePassword(true);
          if (window.location.pathname !== CHANGE_PASSWORD_PATH) {
            window.location.href = CHANGE_PASSWORD_PATH;
          }
        } else if (isMfaEnrollmentRequiredError(error)) {
          // The company requires MFA (tracker item 63): only the security page works until it is set up
          storage.saveMfaEnrollmentRequired(true);
          if (window.location.pathname !== SECURITY_PATH) {
            window.location.href = SECURITY_PATH;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setAuthToken(token: string): void {
    this.token = token;
    this.http.defaults.headers.common.Authorization = `Bearer ${token}`;
  }

  getAuthToken(): string | null {
    return this.token;
  }

  clearAuthToken(): void {
    this.token = null;
    delete this.http.defaults.headers.common.Authorization;
  }
}
